'use client'

import { useState } from 'react'
import { ChevronDown, ChevronUp, Link2, FileText } from 'lucide-react'

interface ParsedSection {
  heading: string
  type: string
  wordCount: number
  linkCount: number
  contentPreview: string
  fullContent: string
}

interface SectionBreakdownProps {
  sections: ParsedSection[]
}

const TYPE_COLORS: Record<string, string> = {
  'Author/Founder': '#F90B8A',
  Company: '#008dcc',
  'Products/Services': '#8b5cf6',
  Technology: '#20808d',
  'Blog/Content': '#FDC304',
  'URLs/Links': '#22c55e',
  Policy: '#f97316',
  Contact: '#F90B8A',
  General: '#64748b',
}

export default function SectionBreakdown({ sections }: SectionBreakdownProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  if (sections.length === 0) {
    return (
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
        <h2 className='text-lg font-semibold text-foreground mb-3 flex items-center gap-2'>
          <FileText size={20} style={{ color: '#8b5cf6' }} />
          Parsed Sections
        </h2>
        <p className='text-sm text-muted-foreground'>
          No structured sections were detected. Consider adding ## headings to
          organize your llms.txt content.
        </p>
      </div>
    )
  }

  return (
    <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
      <h2 className='text-lg font-semibold text-foreground mb-4 flex items-center gap-2'>
        <FileText size={20} style={{ color: '#8b5cf6' }} />
        Parsed Sections
        <span className='text-xs font-normal text-muted-foreground ml-1'>
          ({sections.length})
        </span>
      </h2>

      <div className='space-y-3'>
        {sections.map((section, i) => {
          const isExpanded = expandedIndex === i
          const typeColor = TYPE_COLORS[section.type] || TYPE_COLORS.General

          return (
            <div
              key={i}
              className='bg-background border border-border/60 rounded-xl overflow-hidden'
            >
              <button
                onClick={() => setExpandedIndex(isExpanded ? null : i)}
                className='w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors'
              >
                <div className='flex items-center gap-3 min-w-0 flex-1'>
                  <span
                    className='text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 shrink-0'
                    style={{
                      backgroundColor: `${typeColor}15`,
                      color: typeColor,
                    }}
                  >
                    {section.type}
                  </span>
                  <span className='text-sm font-medium text-foreground truncate'>
                    {section.heading}
                  </span>
                </div>

                <div className='flex items-center gap-3 shrink-0 ml-3'>
                  <span className='text-xs text-muted-foreground'>
                    {section.wordCount}w
                  </span>
                  {section.linkCount > 0 && (
                    <span className='flex items-center gap-1 text-xs text-muted-foreground'>
                      <Link2 size={12} />
                      {section.linkCount}
                    </span>
                  )}
                  {isExpanded ? (
                    <ChevronUp size={16} className='text-muted-foreground' />
                  ) : (
                    <ChevronDown size={16} className='text-muted-foreground' />
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className='px-4 pb-4 border-t border-border/40'>
                  <pre className='text-xs text-foreground/70 leading-relaxed whitespace-pre-wrap font-mono mt-3 max-h-64 overflow-auto'>
                    {section.fullContent}
                  </pre>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
